// src/pages/common/NewReceiptItemsSidePanelContent.tsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ScrollableTable from '../../components/common/ScrollableTable';
import CloseButtonSidePanel from '../../components/common/CloseButtonSidePanel';
import IconAddItemsButton from '../../components/common/IconAddItemsButton';
import SketchButton from '../../components/common/SketchButton';
import Modal from '../../components/common/Modal';
import SelectStoreAndInvoiceModal from './SelectStoreAndInvoiceModal';
import SearchPendingReceiptProductModalContent, {
  type SelecaoTudoPendentes,
} from './SearchPendingReceiptProductModalContent';
import { ReceivedItem } from '../../types/recebimento/PendingReceiptItem';
import './NewReceiptItemsSidePanelContent.css';
import { useApiCall } from '../../hooks/useApiCall';
import { showAlert } from '../../contexts/DialogContext';
import { Toast } from '../../contexts/ToastContext';
import { fragmentoSelectAll } from '@/utils/selectAll';
import { logger } from '@/utils/logger';

// Os dois modos de montar o rascunho são EXCLUDENTES, e é o contrato que manda:
// com `selectAll=true` o servidor ignora o campo `itens`
// (RecebimentoConferenciaItemService:903-909), e um override só se aplica a id já
// dentro do conjunto resolvido — então o carrinho montado à mão não teria como
// sobreviver ao select-all. Em vez de deixar o servidor descartá-lo em silêncio,
// a tela impede a combinação e diz por quê.
const AVISO_CARRINHO_ATIVO =
  'Você já adicionou itens manualmente. Salve ou remova o rascunho atual para selecionar todos entre páginas.';

interface NewReceiptItemsSidePanelContentProps {
  theme: 'dark' | 'light';
  onClose: () => void;
  onPersistSuccess?: () => void;
}

const NewReceiptItemsSidePanelContent: React.FC<NewReceiptItemsSidePanelContentProps> = ({
  theme,
  onClose,
  onPersistSuccess,
}) => {
  const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null);
  const [selectedStoreName, setSelectedStoreName] = useState<string>('');
  const [invoiceNumber, setInvoiceNumber] = useState<string>('');
  const [tipoRecebimento, setTipoRecebimento] = useState<string>('');
  const [receivedItems, setReceivedItems] = useState<ReceivedItem[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [tipoRecebimentoExibicao, setTipoRecebimentoExibicao] = useState<string>('');
  const { callApi, isLoading: isSaving } = useApiCall();

  // Superfície 9. A seleção entre páginas não vira carrinho: o servidor resolve o
  // conjunto ao salvar, então o que a tela guarda é a INTENÇÃO, ao lado (nunca
  // junto) do carrinho enumerado — ver AVISO_CARRINHO_ATIVO.
  //
  // Sem polling e sem trava, por desenho: POST /itens/criar é SEMPRE inline
  // (ADR-044/D3 — não há conferência-pai para o motor de lote proteger). Por isso
  // esta superfície usa o mesmo useApiCall do modo enumerado, e não
  // useOperacaoLote: aqui não existe 202 para distinguir.
  const [selecaoTudo, setSelecaoTudo] = useState<SelecaoTudoPendentes | null>(null);

  useEffect(() => {
    if (!selectedStoreId && !invoiceNumber) {
      setShowModal(true);
    }
  }, [selectedStoreId, invoiceNumber]);

const handleSaveModal = (storeId: number, storeName: string, invoice: string, tipo: string) => {
  setSelectedStoreId(storeId);
  setSelectedStoreName(storeName);
  setInvoiceNumber(invoice);
  setTipoRecebimento('ordemCompra'); // TODO: tornar dinamico quando modulo transferencia for desenvolvido
  setTipoRecebimentoExibicao(tipo); // exibe na tela
  setShowModal(false);
  };

  const handleItemsAdded = (newItems: ReceivedItem[]) => {
    setReceivedItems(prev => {
      const updated = [...prev];
      newItems.forEach(newItem => {
        const index = updated.findIndex(item => item.ordemCompraRomaneioItemId === newItem.ordemCompraRomaneioItemId);
        const added = newItem.qtdRecebida;
        if (index !== -1) {
          updated[index].qtdRecebida += added;
          updated[index].qtdRecebidaTotal += added;
          updated[index].qtdReceber -= added;
          const excess = Math.max(0, updated[index].qtdRecebidaTotal - updated[index].qtdCompraTransferenciaOriginal);
          updated[index].qtdRecebidaTotal -= excess;
          updated[index].qtdRecebida -= excess;
          updated[index].qtdReceber = Math.max(0, updated[index].qtdCompraTransferenciaOriginal - updated[index].qtdRecebidaTotal);
          // Ajuste para acumular qtdRecebida e qtdRecebidaTotal, e subtrair de qtdReceber. Calculado excesso para subtrair de ambos qtdRecebida e qtdRecebidaTotal, prevenindo excedentes em cenários inválidos.
        } else {
          const newReceived = {
            ...newItem,
            qtdRecebidaTotal: newItem.qtdRecebidaTotal + added,
            qtdReceber: newItem.qtdReceber - added,
          };
          const excess = Math.max(0, newReceived.qtdRecebidaTotal - newReceived.qtdCompraTransferenciaOriginal);
          newReceived.qtdRecebidaTotal -= excess;
          newReceived.qtdRecebida -= excess;
          newReceived.qtdReceber = Math.max(0, newReceived.qtdCompraTransferenciaOriginal - newReceived.qtdRecebidaTotal);
          updated.push(newReceived);
          // Ajuste inicial para novos itens, aplicando delta na adição. Calculado excesso para subtrair de ambos qtdRecebida e qtdRecebidaTotal, prevenindo excedentes em cenários inválidos.
        }
      });
      return updated;
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    clearMemoryAndClose();
  };

  const clearMemoryAndClose = () => {
    setSelectedStoreId(null);
    setSelectedStoreName('');
    setInvoiceNumber('');
    setTipoRecebimento('');
    setReceivedItems([]);
    setSelecaoTudo(null);
    onClose();
  };

  const handleSelecionarTudo = (selecao: SelecaoTudoPendentes) => setSelecaoTudo(selecao);

  const handleDesfazerSelecaoTudo = () => setSelecaoTudo(null);

  // Cria o rascunho com o conjunto que o SERVIDOR resolve, no mesmo corpo do modo
  // enumerado — só que por `selectAll` em vez da lista. `tipo`, `lojaEntregaId` e
  // `nroNotaFiscal` migram da query string para o corpo, que é onde a rota nova
  // os recebe; o valor de cada um é o mesmo de hoje, inclusive a NF, que esta
  // tela sempre enviou (ao contrário da adição a rascunho existente).
  const salvarSelecaoTudo = async () => {
    if (!selecaoTudo) return;

    const payload = {
      ...fragmentoSelectAll<typeof selecaoTudo.filtro, number>({
        filtro: selecaoTudo.filtro,
        excludeIds: selecaoTudo.excludeIds,
      }),
      ...(selecaoTudo.overrides.length > 0 ? { overrides: selecaoTudo.overrides } : {}),
      tipo: tipoRecebimento,
      lojaEntregaId: selectedStoreId,
      nroNotaFiscal: invoiceNumber,
    };

    try {
      await callApi('post', '/v1/recebimento-conferencia/itens/criar', payload, {
        onSuccess: (conferenciaId) => {
          // Mesma mensagem do modo enumerado: a rota devolve o id da conferência
          // criada, não decisão por item — não há contadores a derivar aqui, e a
          // ausência de ramo 202 é definitiva (ADR-044/D3).
          Toast.success(`Conferência nº: ${conferenciaId} criada com sucesso.`);
          onPersistSuccess?.();
          clearMemoryAndClose();
        },
      });
    } catch (error) {
      logger.error('Erro ao criar conferência:', error);
      const backendMsg = axios.isAxiosError<{ error?: string }>(error) ? error.response?.data?.error : undefined;
      await showAlert(backendMsg || 'Erro ao criar conferência. Tente novamente.', { variant: 'error' });
    }
  };

  const handleSave = async () => {
    if (selecaoTudo) {
      await salvarSelecaoTudo();
      return;
    }

    if (receivedItems.length === 0) {
      await showAlert('Adicione pelo menos um item.', { variant: 'warning' });
      return;
    }

    try {
      const queryParams = new URLSearchParams({
        tipo: tipoRecebimento,
        lojaEntregaId: selectedStoreId?.toString() || '',
        nroNotaFiscal: invoiceNumber,
      });
      const endpoint = `/v1/recebimento-conferencia/itens?${queryParams.toString()}`;
      const body = receivedItems.map(item => ({
        ordemCompraItemId: item.ordemCompraRomaneioItemId,
        qtdRecebida: item.qtdRecebida,
      }));

      await callApi('post', endpoint, body, {
        onSuccess: (conferenciaId) => {
          Toast.success(`Conferência nº: ${conferenciaId} criada com sucesso.`);

          onPersistSuccess?.();
          clearMemoryAndClose();
        },
      });
    } catch (error) {
      logger.error('Erro ao criar conferência:', error);
      const backendMsg = axios.isAxiosError<{ error?: string }>(error) ? error.response?.data?.error : undefined;
      await showAlert(backendMsg || 'Erro ao criar conferência. Tente novamente.', { variant: 'error' });
    }
    // Removido finally: gerenciado pelo hook.
  };
// Função para enviar POST à rota /v1/recebimento-conferencia/itens, com query params e body mapeado dos itens recebidos, exibindo alert de sucesso com ID retornado.

  const columns = [
    { header: 'Código', render: (item: ReceivedItem) => item.codProduto },
    { header: 'Cód. Categoria', render: (item: ReceivedItem) => item.codCategoriaProduto },
    { header: 'Nome do Produto', render: (item: ReceivedItem) => item.nomeProduto },
    { header: 'Categoria', render: (item: ReceivedItem) => item.nomeCategoria },
    { header: 'Qtd. Total', render: (item: ReceivedItem) => item.qtdCompraTransferenciaOriginal },
    { header: 'Requisição', render: (item: ReceivedItem) => item.requisicaoId },
    {
      header: 'Prioridade',
      render: (item: ReceivedItem) => {
        const prioridade = item.prioridade || 'Não definida';
        if (prioridade === 'URGENTE') {
          return <span className="urgent-priority">{prioridade}</span>;
        }
        return prioridade;
      },
    },
    { header: 'Loja Solicitante', render: (item: ReceivedItem) => item.nomeFantasiaLojaSoliciante },
    { header: 'Status Recebimento', render: (item: ReceivedItem) => item.statusRecebimento },
    { header: 'Qtd. Receber', render: (item: ReceivedItem) => item.qtdReceber },
    { header: 'Qtd. Total Recebido', render: (item: ReceivedItem) => item.qtdRecebidaTotal },
    { header: 'Qtd. Recebida', render: (item: ReceivedItem) => item.qtdRecebida },
  ];

  return (
    <div className={`new-receipt-items-sidepanel-content ${theme}`}>
      <CloseButtonSidePanel onClick={onClose} className="close-button-absolute" disabled={isSaving}/>
      <div className="title-table">
        <div>
          <div className="facelet-h3">
            <h3>Tipo de recebimento: {tipoRecebimentoExibicao}</h3>
          </div>
          <div className="facelet-h3">
            <h3>Recebendo na loja: {selectedStoreName}</h3>
          </div>
          <div className="facelet-p">
            <p>Nota Fiscal nº: {invoiceNumber}</p>
          </div>
        </div>
        <div className='right-facelet'>
          <IconAddItemsButton
            onClick={() => setShowSearchModal(true)}
            title={
              selecaoTudo
                ? 'Desfaça a seleção de todos os itens para adicionar item a item'
                : 'Adicionar Itens'
            }
            className="icon-box-open-button"
            disabled={!!selecaoTudo}
          />
        </div>
      </div>

      <ScrollableTable<ReceivedItem>
        data={receivedItems}
        hasMore={false}
        loadMore={() => {}}
        isLoading={false}
        columns={columns}
        onSelectChange={() => {}}
        // Com a seleção entre páginas ativa não há linhas a mostrar: os itens só
        // existem depois que o servidor resolve o conjunto, ao salvar. Dizer
        // "nenhum item adicionado" ali seria falso — o rascunho tem conteúdo, e o
        // "até" é o que impede a frase de virar promessa (ADR-030 item 3).
        emptyMessage={
          selecaoTudo
            ? `Selecionados até ${selecaoTudo.totalEstimado} itens do filtro de fornecedor. Os itens são resolvidos ao salvar o rascunho.`
            : 'Nenhum item adicionado ainda.'
        }
        showCheckboxes={false}
      />

      <div className="form-actions">
        {/* Botão próprio em vez do GiveUpButton: ele tem o rótulo "Desistir"
            fixo, que aqui seria ambíguo com desistir do rascunho inteiro, e
            acrescentar `label` mexeria num componente compartilhado por várias
            telas — fora do escopo desta frente. O estilo mora no CSS DESTA tela,
            não na classe do outro componente: aquele CSS só entra no bundle da
            rota se o componente for carregado nela. */}
        {selecaoTudo && (
          <button
            type="button"
            className="desfazer-selecao-button"
            onClick={handleDesfazerSelecaoTudo}
            disabled={isSaving}
          >
            Desfazer seleção
          </button>
        )}
        <SketchButton
          isLoading={isSaving}
          onClick={handleSave}
          disabled={isSaving || (receivedItems.length === 0 && !selecaoTudo)}
        />
      </div>

      <Modal isOpen={showModal} onClose={handleCloseModal} theme={theme}>
        <SelectStoreAndInvoiceModal
          theme={theme}
          onSave={handleSaveModal}
          onClose={handleCloseModal}
        />
      </Modal>

      <Modal isOpen={showSearchModal} onClose={() => setShowSearchModal(false)} theme={theme}>
        <SearchPendingReceiptProductModalContent
          theme={theme}
          onClose={() => setShowSearchModal(false)}
          tipo={tipoRecebimento}
          lojaId={selectedStoreId}
          onItemsAdded={handleItemsAdded}
          receivedItems={receivedItems}
          onSelecionarTudo={handleSelecionarTudo}
          motivoSelecaoTudoIndisponivel={
            receivedItems.length > 0 ? AVISO_CARRINHO_ATIVO : undefined
          }
        />
      </Modal>
    </div>
  );
};

export default NewReceiptItemsSidePanelContent;




